import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { TableField } from '@app/modules/security-setup/models/table-field.model';
import { Table } from '@app/modules/security-setup/models/table.model';
import { SecuritySetupService } from '@app/modules/security-setup/services/security-setup.service';
import { IntegrationsService } from '@app/modules/site-settings-integrations/services/integrations.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SelectSearchState } from '@app/shared/components/select-search/select-search-state';
import { TranslateService } from '@ngx-translate/core';
import { finalize, forkJoin, from, Observable } from 'rxjs';

@Component({
  selector: 'app-adp-mapping-dialog',
  templateUrl: './adp-mapping-dialog.component.html',
  styleUrls: ['./adp-mapping-dialog.component.scss']
})
export class AdpMappingDialogComponent implements OnInit {
  public integrationId: string;
  public integrationMapping?: any;
  public form: any;
  public tableGroups: Array<any>;
  public selectedTable: Table & { fields?: TableField[] };
  public fieldList: Array<TableField>;
  public selectedField: TableField;
  loadingTables: boolean;
  tables: any;
  loadingFields: boolean;
  adpFields: string[] = [];

  // Both lists are long, so each has a search box; the current field stays listed while a search hides it.
  adpFieldSearch = new SelectSearchState<string>(
    () => this.adpFields,
    field => field,
    () => this.form.get('integrationFieldId').value
  );
  tableFieldSearch = new SelectSearchState<TableField>(
    () => this.selectedTable?.fields ?? [],
    field => field.name,
    () => this.selectedTable?.fields?.find(field => field.id === this.form.get('tableField').value)
  );

  constructor(
      private fb: UntypedFormBuilder,
      private translate: TranslateService,
      private overlayService: OverlayService,
      private snackbarService: SnackbarService,
      private securitySetupService: SecuritySetupService,
      private integrationService: IntegrationsService,
      private dialogRef: MatDialogRef<AdpMappingDialogComponent>,
      @Inject(MAT_DIALOG_DATA) data
  ) {
      this.integrationId = data.integrationId;
      this.integrationMapping = data.integrationMapping;
      this.createForm();
  }

  ngOnInit(): void {
    this.getTablesAndFields();
    this.getAdpFields();

    // The option only means something for a lookup field, so don't carry it over to one that isn't.
    this.form.get('tableField').valueChanges.subscribe(() => {
      if (!this.isLookupField) {
        this.form.get('createLookupIfMissing').setValue(false, { emitEvent: false });
      }
    });
  }

  // Same test the spreadsheet import's column mapping uses: the catalog gives a lookup field a ListGroup.
  get isLookupField(): boolean {
    const table = this.tables?.find(t => t.id === this.selectedTable?.id);
    const field = table?.fields?.find(f => f.id === this.form.get('tableField').value);

    return !!field?.properties?.some(prop => prop.property === 'ListGroup' && !!prop.value);
  }

  createForm() {
      this.form = this.fb.group({
        table: [this.integrationMapping ? this.integrationMapping.table?.id : null, Validators.required],
        tableField: [this.integrationMapping ? this.integrationMapping.tableField?.id : null, Validators.required],
        integrationFieldId: [this.integrationMapping ? this.integrationMapping.integrationFieldId : null, Validators.required],
        createLookupIfMissing: [this.integrationMapping ? !!this.integrationMapping.createLookupIfMissing : false],
        transformations: this.fb.array([])
      });

      if (this.integrationMapping?.transformations && this.integrationMapping.transformations.length > 0) {
        this.integrationMapping.transformations.forEach(transformation => {
          this.addTransformation(transformation);
        });
      }
  }

  removeTransformation(index) {
    this.form.get('transformations').removeAt(index);
  }

  addTransformation(transformation?: {hubbub: string, integrationTarget: string}) {
    this.form.get('transformations').push(this.fb.group({
      hubbub: [ transformation ? transformation.hubbub : '', Validators.required],
      integrationTarget: [ transformation ? transformation.integrationTarget : '', Validators.required]
    }));
  }

  getAdpFields() {
    this.integrationService.getAdpMappingFields(this.integrationId).subscribe(
      res => {
        this.adpFields = res;
        this.adpFieldSearch.refresh();
      }
    );
  }

  getTablesAndFields() {
    this.loadingTables = true;

    this.integrationService.getAdpMappingsHubbubTables(this.integrationId)
    .pipe(
      finalize( () => {
          this.loadingTables = false;
          this.getTableFields();
        }
      )
    )
    .subscribe(
      res => {
        this.tables = res.data;
      }
    );
  }

  getTableFields() {
    this.loadingFields = true;

    let observables: Observable<any>[] = [];

    this.tables.forEach(table => {
      observables.push(from(this.securitySetupService.getAllFields(table.id)));
    });

    forkJoin(observables)
    .pipe(
      finalize( () => {
          this.loadingFields = false;
          this.createTablesMap();
        }
      )
    )
    .subscribe(
      (res) => {
        res.forEach((fields, index) => {
            this.tables[index].fields = fields.data;
          }
        )
      }
    );
  }

  createTablesMap() {
    let tableList = this.tables;
    let tableMap = [];
    tableList.map(table => {
        if (table.parentTable != null) {
            let groupIndex = tableMap.findIndex(t => t.groupId === table.parentTable.id);

            if (groupIndex >= 0) {
                tableMap[groupIndex].tables.push(table);
            } else {
                tableMap.push({groupName: table.parentTable.name, groupId: table.parentTable.id, tables: [table]});
            }
        } else {
            tableMap.push({groupName: table.name, groupId: table.id, tables: [table]});
        }
    });
    this.tableGroups = tableMap;

    if(this.integrationMapping?.tableField?.id) {
      this.selectedTable = this.tables.find(table => table.id === this.integrationMapping.table.id);
      this.tableFieldSearch.refresh();
    }
  }

  handleSelectTableClick(tableId) {
      this.selectedTable = this.tables.find(table => table.id === tableId);
      this.selectedField = null;
      this.form.get('tableField').setValue(null);
      this.tableFieldSearch.refresh();
  }

  handleSelectFieldClick(tableField) {
      this.selectedField = tableField;
      this.form.get('tableField').setValue(tableField.id);
  }

  save() {
      this.overlayService.show();

      let mappingPayload = this.form.value;

      if (this.integrationMapping) {
          this.integrationService.updateAdpMapping(this.integrationId, this.integrationMapping.id, mappingPayload)
          .subscribe(
            {
                next: (v) => {
                    this.snackbarService.openSnackBar(this.translate.instant('IntegrationConfigUpdated'), 'clear', 'success');
                    this.dialogRef.close(true);
                },
                error: (e) => this.overlayService.hide(),
                complete: () => this.overlayService.hide()
            }
          );
      } else {
          this.integrationService.createAdpMapping(this.integrationId, mappingPayload)
          .subscribe(
            {
                next: (v) => {
                    this.snackbarService.openSnackBar(this.translate.instant('IntegrationConfigUpdated'), 'clear', 'success');
                    this.dialogRef.close(true);
                },
                error: (e) => this.overlayService.hide(),
                complete: () => this.overlayService.hide()
            }
          );
      }
  }

  close() {
      this.dialogRef.close();
  }
}
